/**
 * MiniAppShare — the "Share MiniApp" flow, shared by every surface that offers
 * it (desktop drive context menu, mobile file sheet, editor toolbar).
 *
 * A "Share MiniApp" action is shown on an HTML file that lives inside a
 * `.miniapp/` folder (see `canShareMiniApp` in ../client). Clicking it should
 * share the CONTAINING mini-app, not the HTML file — i.e. open the existing
 * ShareLinksDialog targeting the `.miniapp` FOLDER (which mints a `kind:'app'`
 * share of the folder). When the file is inside NESTED mini-apps, we first ask
 * which one.
 *
 * `useMiniAppShare()` owns that branch + both dialogs so callers stay tiny:
 *
 *     const miniApp = useMiniAppShare()
 *     // ...in a menu item: onClick={() => miniApp.openMiniAppShare(ancestors)}
 *     // ...in the render:  {miniApp.node}
 *
 * `ancestors` is the `.miniapp` crumb list from `miniappAncestors(crumbs)` —
 * top-level → nearest. One entry ⇒ share it directly; two+ ⇒ show the picker.
 */
import { useCallback, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ChevronRight, X } from 'lucide-react'
import { useTheme, getThemeClass, PDAppIcon } from '@papan/core-ui/ui'
import { stripMiniappFolderExt } from '@papan/types'
import { shareKindForFile, type CrumbLike } from '../client/index.js'
import { ShareLinksDialog } from './ShareLinksDialog.js'

type FlowState =
  | { kind: 'pick'; ancestors: CrumbLike[] }
  | { kind: 'share'; folder: CrumbLike }
  | null

export interface UseMiniAppShare {
  /** Open the flow for a file's `.miniapp` ancestor list (top-level → nearest).
   *  1 ancestor → share it directly; 2+ → show the picker. No-op on empty. */
  openMiniAppShare: (ancestors: readonly CrumbLike[]) => void
  /** Render this once in the caller's tree — the picker / share dialog live here. */
  node: ReactNode
}

export function useMiniAppShare(): UseMiniAppShare {
  const [state, setState] = useState<FlowState>(null)

  const openMiniAppShare = useCallback((ancestors: readonly CrumbLike[]) => {
    if (ancestors.length === 0) return
    if (ancestors.length === 1) setState({ kind: 'share', folder: ancestors[0] })
    else setState({ kind: 'pick', ancestors: [...ancestors] })
  }, [])

  const close = useCallback(() => setState(null), [])

  let node: ReactNode = null
  if (state?.kind === 'pick') {
    node = (
      <MiniAppPickerDialog
        ancestors={state.ancestors}
        onPick={(folder) => setState({ kind: 'share', folder })}
        onClose={close}
      />
    )
  } else if (state?.kind === 'share') {
    node = (
      <ShareLinksDialog
        fileId={state.folder.id}
        fileName={state.folder.name}
        isFolder
        isAppEligible={shareKindForFile({ name: state.folder.name, isFolder: true }) === 'app'}
        onClose={close}
      />
    )
  }

  return { openMiniAppShare, node }
}

/**
 * Picker for the NESTED case — the file is inside more than one `.miniapp`. Lists
 * the mini-apps NEAREST-first (the immediate container on top). Themed like the
 * other share overlays: `themeClass` goes on the PANEL (theme classes paint an
 * opaque bg), never the inset-0 wrapper. z-[110] clears the ShareLinksDialog
 * overlay (z-[100]) in case both ever coexist.
 */
function MiniAppPickerDialog({
  ancestors,
  onPick,
  onClose,
}: {
  ancestors: CrumbLike[]
  onPick: (folder: CrumbLike) => void
  onClose: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  // Crumbs arrive top-level → nearest — i.e. OUTERMOST → innermost, which is the
  // containment order top-down. Render as an indented tree so the nesting is
  // visible; the row's depth = its index. Clicking any node shares THAT mini-app.

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-md ws-anim-fade-in"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
        role="button"
        tabIndex={-1}
        aria-label="Close"
      />
      <div className={`relative z-10 ${themeClass} ws-glass-modal ws-anim-modal-in w-full max-w-sm rounded-xl overflow-hidden`}>
        <div className="flex items-start justify-between gap-3 p-4 pb-1">
          <div className="min-w-0">
            <h2 className="themed-text text-sm font-semibold leading-tight">Which PDApp?</h2>
            <p className="themed-text-secondary text-xs mt-0.5">This file is nested inside these PDApps. Choose which one to share.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="themed-icon shrink-0 rounded p-1 hover:themed-bg-tertiary"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        {/* Containment list: outermost at the top, each nested mini-app indented
            one level deeper (depth = index). The mini-apps are the prominent
            element — larger rows, roomy spacing, a trailing chevron. Every row
            is a share target. */}
        <div className="px-3 pb-3 pt-1 flex flex-col gap-1.5" role="tree">
          {ancestors.map((folder, depth) => (
            // Indent lives on the WRAPPER so the full-width card fills the
            // REMAINING space — a nested card steps in without overflowing.
            <div key={folder.id} style={{ paddingLeft: `${depth * 20}px` }}>
              <button
                type="button"
                role="treeitem"
                aria-level={depth + 1}
                onClick={() => onPick(folder)}
                className="flex items-center justify-between gap-2 w-full text-left px-3 py-3 text-sm font-medium rounded-lg cursor-pointer themed-bg-tertiary hover:themed-bg-hover themed-text"
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <PDAppIcon size={18} className="shrink-0 text-pink-500" />
                  <span className="truncate">{stripMiniappFolderExt(folder.name)}</span>
                </span>
                <ChevronRight size={16} className="shrink-0 themed-text-muted" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
